import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { getStorefrontConfig } from '../lib/dropify'
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../lib/seo'

export const Route = createFileRoute('/privasi')({
  loader: async () => ({
    config: await getStorefrontConfig().catch(() => null),
  }),
  head: () => ({
    meta: [
      { title: 'Kebijakan Privasi Pelanggan | Dropify Grosir' },
      { name: 'description', content: 'Kebijakan privasi Dropify terkait pengumpulan, penggunaan, dan perlindungan informasi pelanggan.' },
      { property: 'og:title', content: 'Kebijakan Privasi Pelanggan | Dropify Grosir' },
      { property: 'og:description', content: 'Kebijakan privasi Dropify terkait pengumpulan, penggunaan, dan perlindungan informasi pelanggan.' },
      { property: 'og:url', content: absoluteUrl('/privasi') },
      { property: 'og:image', content: DEFAULT_OG_IMAGE },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Kebijakan Privasi Pelanggan | Dropify Grosir' },
      { name: 'twitter:description', content: 'Kebijakan privasi Dropify terkait pengumpulan, penggunaan, dan perlindungan informasi pelanggan.' },
      { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
    ],
    links: [
      { rel: 'canonical', href: absoluteUrl('/privasi') },
    ],
  }),
  component: PrivasiPage,
})

function PrivasiPage() {
  const { config } = Route.useLoaderData()

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">Privasi</span>
          </nav>
          <h1 className="text-4xl font-black text-white uppercase tracking-tight mb-2">Privasi</h1>
          <p className="text-slate-300 text-sm max-w-2xl">
            Cara Dropify mengelola informasi yang Anda berikan saat mengakses website, bertanya produk, atau berkomunikasi dengan tim kami.
          </p>
        </div>
      </div>

      <WhatsAppChannelSection />

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-10 space-y-8">
          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">1. Informasi yang Kami Terima</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Kami dapat menerima informasi seperti nama, nomor WhatsApp, alamat email, alamat pengiriman, detail pesanan, dan pesan yang Anda kirimkan saat menghubungi Dropify. Website juga dapat memproses data teknis dasar seperti perangkat, browser, halaman yang dikunjungi, dan sumber kunjungan untuk kebutuhan keamanan dan peningkatan layanan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">2. Penggunaan Informasi</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Informasi digunakan untuk menjawab pertanyaan, mengonfirmasi pesanan, membantu proses pembayaran dan pengiriman, memberikan pembaruan terkait produk, serta meningkatkan pengalaman pengguna di website Dropify.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">3. Komunikasi dan Layanan Pihak Ketiga</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Pemesanan dan komunikasi transaksi dilakukan melalui WhatsApp resmi Dropify. Informasi yang diperlukan dapat diteruskan kepada penyedia layanan yang membantu pembayaran, pengiriman, analitik, atau pengoperasian website. Data yang diberikan dibatasi sesuai fungsi penyedia tersebut.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">4. Cookie dan Analitik</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Website dapat menggunakan cookie, tag, atau teknologi serupa untuk memahami performa halaman, mengukur kunjungan, menjaga fungsi website, dan membantu evaluasi promosi. Anda dapat mengatur preferensi cookie melalui pengaturan browser masing-masing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">5. Penyimpanan dan Keamanan</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Kami menyimpan informasi selama masih diperlukan untuk kebutuhan layanan, administrasi, komunikasi pelanggan, dan pencatatan transaksi. Dropify menerapkan langkah wajar untuk menjaga informasi dari akses, penggunaan, atau pengungkapan yang tidak semestinya.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">6. Hak dan Kontak Pelanggan</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Anda dapat menghubungi kami untuk meminta pembaruan, koreksi, atau penghapusan informasi tertentu sepanjang permintaan tersebut dapat diproses sesuai kebutuhan layanan dan kewajiban pencatatan. Untuk pertanyaan privasi, silakan hubungi admin Dropify melalui WhatsApp atau email resmi yang tercantum di website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-3">7. Perubahan Kebijakan</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Kebijakan privasi ini dapat diperbarui sewaktu-waktu agar tetap sesuai dengan operasional Dropify. Versi terbaru yang tampil pada halaman ini menjadi acuan yang berlaku.
            </p>
          </section>
        </div>
      </main>

      <Footer config={config} />
    </div>
  )
}
